import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  MenuItem,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React from "react";
import * as yup from "yup";

import type {
  SimulateDonationsMutation,
  SimulateDonationsMutationVariables,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  FormikTextField,
  FormikSwitchField,
  FormikSelect,
  FormikErrorBanner,
  FormikSubmitButton,
  submitErrorField,
} from "~frontend/components/forms";
import { SIMULATE_DONATIONS } from "~frontend/graphql/integration";
import useMutation from "~frontend/util/useMutation";

export interface SimulateDonationsDialogProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  integrationId: string;
}

interface FormValues {
  count: number;
  generateRefcodes: boolean;
  delayMs: number;
  amountInCents: number | "";
  recurringPeriod: string;
  textMessageOption: string;
  donorFirstname: string;
  donorLastname: string;
  donorPhone: string;
  donorEmail: string;
}

const validationSchema = yup.object().shape({
  count: yup
    .number()
    .required("Required")
    .min(1, "Must be at least 1")
    .max(1000, "Must be at most 1000")
    .integer("Must be a whole number"),
  generateRefcodes: yup.boolean().required("Required"),
  delayMs: yup
    .number()
    .required("Required")
    .min(0, "Must be at least 0")
    .max(5000, "Must be at most 5000")
    .integer("Must be a whole number"),
  amountInCents: yup
    .number()
    .nullable()
    .notRequired()
    .test(
      "non-zero-integer",
      "Must be at least 1 cent and a whole number",
      function (value: number | "" | null | undefined) {
        if (value === null || value === undefined || value === "") {
          return true;
        }
        return value != 0 && Number.isInteger(value);
      }
    ),
  recurringPeriod: yup.string().notRequired(),
  textMessageOption: yup.string().notRequired(),
  donorFirstname: yup.string().notRequired(),
  donorLastname: yup.string().notRequired(),
  donorPhone: yup.string().notRequired(),
  donorEmail: yup.string().email("Must be a valid email").notRequired(),
});

export const SimulateDonationsDialog: React.FC<
  SimulateDonationsDialogProps
> = ({ open, onClose, campaignId, integrationId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [simulateDonations] = useMutation<
    SimulateDonationsMutation,
    SimulateDonationsMutationVariables
  >(SIMULATE_DONATIONS);

  const initialValues: FormValues = {
    count: 1,
    generateRefcodes: true,
    delayMs: 100,
    amountInCents: "",
    recurringPeriod: "",
    textMessageOption: "",
    donorFirstname: "",
    donorLastname: "",
    donorPhone: "",
    donorEmail: "",
  };

  const handleSubmit = async (values: FormValues, formik: any) => {
    try {
      const input = {
        integrationId,
        campaignId,
        count: values.count,
        generateRefcodes: values.generateRefcodes,
        delayMs: values.delayMs,
        amountInCents:
          values.amountInCents !== ""
            ? Number(values.amountInCents)
            : undefined,
        recurringPeriod: values.recurringPeriod || undefined,
        textMessageOption: values.textMessageOption || undefined,
        donorFirstname: values.donorFirstname || undefined,
        donorLastname: values.donorLastname || undefined,
        donorPhone: values.donorPhone || undefined,
        donorEmail: values.donorEmail || undefined,
      };

      const { data, errors } = await simulateDonations({
        variables: { input },
      });

      if (errors) {
        const errorMessage = errors[0]?.message || "Unknown error occurred";
        enqueueSnackbar(`Error: ${errorMessage}`, { variant: "error" });
        formik.setFieldError(submitErrorField, errorMessage);
        return;
      }

      if (data?.simulateDonations) {
        const result = data.simulateDonations;

        if (result.success) {
          enqueueSnackbar(result.message, { variant: "success" });
          formik.resetForm();
          onClose();
        } else {
          // Show partial success or complete failure
          const variant = result.simulatedCount > 0 ? "warning" : "error";
          enqueueSnackbar(result.message, { variant });

          if (result.errors.length > 0) {
            formik.setFieldError(submitErrorField, result.errors.join("; "));
          }
        }

        // Show warnings if any
        if (result.warnings.length > 0) {
          result.warnings.forEach(warning => {
            enqueueSnackbar(`Warning: ${warning}`, { variant: "warning" });
          });
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      enqueueSnackbar(`Error: ${errorMessage}`, { variant: "error" });
      formik.setFieldError(submitErrorField, errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Simulate Donations</DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner />

              <FlexBox flexDirection="column" gap={3}>
                <Typography variant="body2" color="text.secondary">
                  Generate simulated ActBlue donation webhooks for testing
                  purposes.
                </Typography>

                {/* Basic Settings */}
                <FlexBox flexDirection="column" gap={2}>
                  <Typography variant="h6">Basic Settings</Typography>

                  <FlexBox gap={2}>
                    <FormikTextField
                      name="count"
                      label="Number of Donations"
                      type="number"
                      fullWidth
                      helperText="1-1000 donations to simulate"
                    />

                    <FormikTextField
                      name="delayMs"
                      label="Delay Between Requests (ms)"
                      type="number"
                      fullWidth
                      helperText="0-5000ms delay"
                    />
                  </FlexBox>

                  <FormikSwitchField
                    name="generateRefcodes"
                    label="Generate Refcodes"
                    helperText="Use actual campaign contacts to generate realistic refcodes"
                  />
                </FlexBox>

                {/* Donation Details */}
                <FlexBox flexDirection="column" gap={2}>
                  <Typography variant="h6">
                    Donation Details (Optional Overrides)
                  </Typography>

                  <FlexBox gap={2}>
                    <FormikTextField
                      name="amountInCents"
                      label="Amount (cents)"
                      type="number"
                      fullWidth
                      helperText="Leave empty for random amounts"
                    />

                    <FormikSelect
                      name="recurringPeriod"
                      label="Recurring Period"
                      fullWidth
                    >
                      <MenuItem value="">Random</MenuItem>
                      <MenuItem value="once">One-time</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </FormikSelect>
                  </FlexBox>

                  <FormikSelect
                    name="textMessageOption"
                    label="Text Message Option"
                    fullWidth
                  >
                    <MenuItem value="">Random</MenuItem>
                    <MenuItem value="opt_in">Opt In</MenuItem>
                    <MenuItem value="opt_out">Opt Out</MenuItem>
                    <MenuItem value="unknown">Unknown</MenuItem>
                  </FormikSelect>
                </FlexBox>

                {/* Donor Information */}
                <FlexBox flexDirection="column" gap={2}>
                  <Typography variant="h6">
                    Donor Information (Optional Overrides)
                  </Typography>

                  <FlexBox gap={2}>
                    <FormikTextField
                      name="donorFirstname"
                      label="First Name"
                      fullWidth
                      helperText="Leave empty for random names"
                    />

                    <FormikTextField
                      name="donorLastname"
                      label="Last Name"
                      fullWidth
                    />
                  </FlexBox>

                  <FlexBox gap={2}>
                    <FormikTextField
                      name="donorEmail"
                      label="Email"
                      type="email"
                      fullWidth
                    />

                    <FormikTextField
                      name="donorPhone"
                      label="Phone Number"
                      fullWidth
                    />
                  </FlexBox>
                </FlexBox>
              </FlexBox>
            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <FormikSubmitButton
                submitText="Simulate Donations"
                loadingText="Simulating..."
              />
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};
